import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BootstrapScreen from "../BootstrapScreen";
import { adoptOnboardingDraft } from "../../services/api";
import { getAuthenticatedDestination } from "../authRouting";
import {
  buildLoginRecoveryPath,
  normalizeInternalReturnPath,
  readValidatedNext,
} from "../returnPath";
import useAuthBootstrap from "../useAuthBootstrap";
import { rememberLastUsedAuthProvider } from "../components/ProviderButton";
import { isStartableAuthProvider } from "../providerCapabilities";
import {
  clearAnonymousOnboardingDraft,
  loadAnonymousOnboardingDraft,
  toAnonymousDraftAdoptionPayload,
} from "../../features/onboarding/onboardingDraftStore";

const DISCARDABLE_DRAFT_ERRORS = new Set([
  "DRAFT_INVALID",
  "DRAFT_SCHEMA_UNSUPPORTED",
]);
// Display-only, and never authority: the badge says "this device last used
// Google", not "you are this person".
function readAuthenticatedProvider(search) {
  const providerId = new URLSearchParams(search).get("provider");
  return isStartableAuthProvider(providerId) ? providerId : null;
}

const CONFIRMED_ADOPTION_REASONS = new Set([
  "SERVER_MORE_ADVANCED",
  "ACCOUNT_COMPLETED",
  "DRAFT_INVALID",
  "DRAFT_SCHEMA_UNSUPPORTED",
]);

export default function AuthReturnScreen() {
  const bootstrap = useAuthBootstrap();
  const location = useLocation();
  const navigate = useNavigate();
  const next =
    normalizeInternalReturnPath(location.state?.next) ||
    readValidatedNext(location.search);
  const provider = readAuthenticatedProvider(location.search);
  const startedRef = useRef(false);
  const [unavailable, setUnavailable] = useState(false);

  const finishAuthentication = useCallback(async () => {
    setUnavailable(false);
    try {
      let refreshed = await bootstrap.refresh();
      if (refreshed?.status !== "authenticated") {
        navigate(buildLoginRecoveryPath(next || "/"), { replace: true });
        return;
      }
      // Only now: a callback that did not produce a Session must never mark a
      // provider as last used.
      rememberLastUsedAuthProvider(provider);

      const draft = loadAnonymousOnboardingDraft();
      let adoptionReason = null;
      if (draft) {
        try {
          const result = await adoptOnboardingDraft(
            toAnonymousDraftAdoptionPayload(draft)
          );
          adoptionReason = result?.reason || (result?.adopted ? "ADOPTED" : null);
          if (
            adoptionReason !== "ADOPTED" &&
            !CONFIRMED_ADOPTION_REASONS.has(adoptionReason)
          ) {
            throw new Error("Draft adoption returned an unrecognized response.");
          }
          clearAnonymousOnboardingDraft();
        } catch (error) {
          if (!DISCARDABLE_DRAFT_ERRORS.has(error?.code)) {
            throw error;
          }
          adoptionReason = error.code;
          clearAnonymousOnboardingDraft();
        }
        refreshed = await bootstrap.refresh();
        if (refreshed?.status !== "authenticated") {
          navigate(buildLoginRecoveryPath(next || "/"), { replace: true });
          return;
        }
      }

      const destination = adoptionReason === "ACCOUNT_COMPLETED"
        ? "/"
        : getAuthenticatedDestination(refreshed.onboarding, next);
      navigate(destination, { replace: true });
    } catch (_error) {
      setUnavailable(true);
    }
  }, [bootstrap, navigate, next, provider]);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    finishAuthentication();
  }, [finishAuthentication]);

  return (
    <BootstrapScreen
      status={unavailable ? "unavailable" : "resolving"}
      presentation={unavailable ? "hidden" : "brand"}
      onRetry={finishAuthentication}
    />
  );
}
